import React from "react";
import { Button, Card, Col, Descriptions, Divider, message, Popconfirm, Row, Space, Tag, Typography } from "antd";
import { ArrowLeftOutlined, CopyOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useFirestore } from "react-redux-firebase";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import urls from "urls";
import { FIRESTORE_CAMPAIGNS_TABLE } from "constants/FirestoreConstant";
import Loading from "components/shared-components/Loading";
import _ from "lodash";

const { Title, Text, Paragraph } = Typography;

const CampaignDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const firestore = useFirestore();
  const campaigns = useSelector((state) => state.firestoreReducer.ordered.campaigns);

  const campaign = React.useMemo(() => _.find(campaigns, ["id", id]), [campaigns, id]);

  const handleDelete = async () => {
    try {
      await firestore.collection(FIRESTORE_CAMPAIGNS_TABLE).doc(id).delete();
      message.success(t("campaign_deleted"));
      navigate(urls.campaigns);
    } catch (error) {
      console.error("Error deleting campaign:", error);
      message.error(t("notifications.error_deleting_campaign"));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "default",
      active: "success",
      paused: "warning",
      completed: "error",
    };
    return colors[status] || "default";
  };

  const getPlatformColor = (platform) => {
    const colors = {
      facebook_ads: "blue",
      google_ads: "green",
      instagram_ads: "purple",
      email_marketing: "orange",
      other: "default",
    };
    return colors[platform] || "default";
  };

  const generateUTMUrl = (baseUrl = "https://hetvleescentrum.nl") => {
    if (!campaign) return "";

    const params = new URLSearchParams();
    if (campaign.utmSource) params.append("utm_source", campaign.utmSource);
    if (campaign.utmMedium) params.append("utm_medium", campaign.utmMedium);
    if (campaign.utmCampaign) params.append("utm_campaign", campaign.utmCampaign);
    if (campaign.utmTerm) params.append("utm_term", campaign.utmTerm);
    if (campaign.utmContent) params.append("utm_content", campaign.utmContent);

    return `${baseUrl}?${params.toString()}`;
  };

  const copyUTMUrl = () => {
    const url = generateUTMUrl();
    navigator.clipboard.writeText(url);
    message.success(t("notifications.utm_url_copied"));
  };

  const formatDate = (date) => {
    if (!date) return "-";
    if (date.toDate && typeof date.toDate === "function") {
      return dayjs(date.toDate()).format("DD-MM-YYYY");
    } else if (date instanceof Date) {
      return dayjs(date).format("DD-MM-YYYY");
    } else {
      return dayjs(date).format("DD-MM-YYYY");
    }
  };

  if (!campaigns || !campaign) {
    return <Loading cover="content" />;
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(urls.campaigns)}>
              {t("back")}
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {campaign.name}
            </Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button type="primary" icon={<EditOutlined />} onClick={() => navigate(`${urls.editCampaign}/${id}`)}>
              {t("edit_campaign")}
            </Button>
            <Popconfirm title={t("confirm_delete")} onConfirm={handleDelete} okText={t("yes")} cancelText={t("no")}>
              <Button danger icon={<DeleteOutlined />}>
                {t("delete_campaign")}
              </Button>
            </Popconfirm>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title={t("campaign_details")}>
            <Descriptions column={1} bordered>
              <Descriptions.Item label={t("campaign_name")}>{campaign.name}</Descriptions.Item>

              <Descriptions.Item label={t("platform")}>
                <Tag color={getPlatformColor(campaign.platform)}>{t(campaign.platform)}</Tag>
              </Descriptions.Item>

              <Descriptions.Item label={t("status")}>
                <Tag color={getStatusColor(campaign.status)}>{t(campaign.status)}</Tag>
              </Descriptions.Item>

              <Descriptions.Item label={t("start_date")}>{formatDate(campaign.startDate)}</Descriptions.Item>

              <Descriptions.Item label={t("end_date")}>
                {campaign.isOngoing ? <Tag color="blue">{t("ongoing")}</Tag> : formatDate(campaign.endDate)}
              </Descriptions.Item>

              <Descriptions.Item label={t("budget")}>
                {campaign.budget ? `€${campaign.budget.toLocaleString()}` : "-"}
              </Descriptions.Item>

              {campaign.description && (
                <Descriptions.Item label={t("campaign_description")}>
                  <Paragraph>{campaign.description}</Paragraph>
                </Descriptions.Item>
              )}

              {campaign.objectives && (
                <Descriptions.Item label={t("objectives")}>
                  <Paragraph>{campaign.objectives}</Paragraph>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title={t("campaign_metrics")} style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "24px", fontWeight: "bold", color: "#1890ff" }}>€0</div>
                  <div style={{ color: "#666" }}>{t("spend")}</div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "24px", fontWeight: "bold", color: "#52c41a" }}>0</div>
                  <div style={{ color: "#666" }}>{t("conversions")}</div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "24px", fontWeight: "bold", color: "#faad14" }}>0</div>
                  <div style={{ color: "#666" }}>{t("clicks")}</div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "24px", fontWeight: "bold", color: "#722ed1" }}>0%</div>
                  <div style={{ color: "#666" }}>{t("ctr")}</div>
                </div>
              </Col>
            </Row>
            <Divider />
            <div style={{ textAlign: "center", color: "#666" }}>
              <Text type="secondary">{t("metrics_availability_message")}</Text>
            </div>
          </Card>
        </Col>
      </Row>

      {(campaign.utmSource ||
        campaign.utmMedium ||
        campaign.utmCampaign ||
        campaign.utmTerm ||
        campaign.utmContent) && (
        <Card title={t("utm_parameters")} style={{ marginTop: 16 }}>
          <Descriptions column={2} bordered>
            {campaign.utmSource && <Descriptions.Item label={t("utm_source")}>{campaign.utmSource}</Descriptions.Item>}
            {campaign.utmMedium && <Descriptions.Item label={t("utm_medium")}>{campaign.utmMedium}</Descriptions.Item>}
            {campaign.utmCampaign && (
              <Descriptions.Item label={t("utm_campaign")}>{campaign.utmCampaign}</Descriptions.Item>
            )}
            {campaign.utmTerm && <Descriptions.Item label={t("utm_term")}>{campaign.utmTerm}</Descriptions.Item>}
            {campaign.utmContent && (
              <Descriptions.Item label={t("utm_content")}>{campaign.utmContent}</Descriptions.Item>
            )}
          </Descriptions>

          <Divider />

          <Row align="middle">
            <Col flex="auto">
              <Text strong>{t("generated_utm_url")}:</Text>
              <br />
              <Text code copyable style={{ wordBreak: "break-all" }}>
                {generateUTMUrl()}
              </Text>
            </Col>
            <Col>
              <Button icon={<CopyOutlined />} onClick={copyUTMUrl}>
                {t("copy_url")}
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      <Card title={t("timeline")} style={{ marginTop: 16 }}>
        <Row>
          <Col span={24}>
            <div style={{ color: "#666" }}>
              <Text type="secondary">{t("timeline_placeholder_message")}</Text>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default CampaignDetail;
